/* FIOME — cart drawer + tier/frequency engine (v6).
   v6 replaces the Recharge Storefront Experiences drawer (app embed disabled in
   settings_data.json) with a fully theme-owned drawer. One code path owns every
   mutation, so the stepper/pill races that plagued v3–v5 are structurally gone:
   all actions run through a serial queue → reconcile → single re-render.

   Tier discount is COMPUTED from total subscription bags (1→20%, 2→25%, 3+→30%).
   Frequency gating = a bite per day: 1 bag→30 · 2 bags→30/60 · 3+→30/60/90.
   Plan IDs (Recharge, Jul 2026):
     30d: 1→692089323801  2→692088406297  3+→692089356569
     60d: 1→692128350489  2→692128383257  3+→692128416025
     90d:                                 3+→692128317721 */
(function () {
  var PLANS = {
    30: { 1: 692089323801, 2: 692088406297, 3: 692089356569 },
    60: { 1: 692128350489, 2: 692128383257, 3: 692128416025 },
    90: { 3: 692128317721 }
  };
  var FREQ_OF = { 692128284953: 90 };
  Object.keys(PLANS).forEach(function (f) {
    Object.keys(PLANS[f]).forEach(function (t) { FREQ_OF[PLANS[f][t]] = +f; });
  });
  function tierOf(n) { return n >= 3 ? 3 : n; }
  function allowedFreqs(total) { return total >= 3 ? [30, 60, 90] : (total >= 2 ? [30, 60] : [30]); }
  var FREE_SHIP_THRESHOLD = 6000;   /* cents — one-time orders; subscriptions always ship free */
  /* Flavor variants for the one-tap upsell strip (myfiome, Jul 2026) */
  var FLAVORS = [
    { name: 'Dark Chocolate', id: 51760374513945 },
    { name: 'Vanilla Bean', id: 51760373596441 },
    { name: 'Peanut Butter', id: 49008032284953 },
    { name: 'Banana Cinnamon', id: 49008032252185 }
  ];
  var SUB_PER = { 1: 2800, 2: 2625, 3: 2450 };   /* per-bag subscription price (cents) by tier */
  var ONE_TIME_PER = 3500;
  var SUB_HANDLE = 'fiber-bites';                /* only this product upsells to a plan */

  var chosenFreq = null;
  var q = Promise.resolve();        /* every mutation runs through this serial queue */

  function getCart() { return fetch('/cart.js').then(function (r) { return r.json(); }); }
  function post(url, body) {
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  }
  function money(cents) {
    var v = cents / 100;
    return '$' + (v % 1 ? v.toFixed(2) : v);
  }

  /* Reconcile: every subscription line carries PLANS[freq][tier(totalBags)] */
  function reconcile() {
    return getCart().then(function (cart) {
      var subs = cart.items.filter(function (i) { return i.selling_plan_allocation; });
      var total = subs.reduce(function (s, i) { return s + i.quantity; }, 0);
      if (!total) return cart;
      var freq = chosenFreq || FREQ_OF[subs[0].selling_plan_allocation.selling_plan.id] || 30;
      var ok = allowedFreqs(total);
      if (ok.indexOf(freq) === -1) freq = ok[ok.length - 1];
      chosenFreq = freq;
      var target = PLANS[freq][tierOf(total)];
      var toFix = subs.filter(function (i) { return i.selling_plan_allocation.selling_plan.id !== target; });
      var chain = Promise.resolve();
      toFix.forEach(function (i) {
        chain = chain.then(function () { return post('/cart/change.js', { id: i.key, quantity: i.quantity, selling_plan: target }); });
      });
      return chain.then(function () { return toFix.length ? getCart() : cart; });
    });
  }

  /* Enqueue a mutation; afterwards reconcile once and re-render once */
  function apply(fn) {
    q = q.then(fn).then(reconcile).then(render).catch(function () { return getCart().then(render).catch(function(){}); });
    return q;
  }

  /* ---------- drawer shell ---------- */
  var root = null;
  function ensureShell() {
    if (root) return;
    root = document.createElement('div');
    root.id = 'fiome-cartdrawer-root';
    root.innerHTML =
      '<style>' +
      '#fiome-cd-overlay{position:fixed;inset:0;background:rgba(73,45,23,.45);z-index:998;opacity:0;pointer-events:none;transition:opacity .25s ease;}' +
      '#fiome-cd{position:fixed;top:0;right:0;bottom:0;width:min(430px,94vw);background:#fff;z-index:999;transform:translateX(105%);transition:transform .28s cubic-bezier(.22,.8,.36,1);display:flex;flex-direction:column;font-family:inherit;color:#492d17;box-shadow:-12px 0 40px rgba(73,45,23,.18);}' +
      '.fiome-cd-open #fiome-cd{transform:none;} .fiome-cd-open #fiome-cd-overlay{opacity:1;pointer-events:auto;}' +
      '#fiome-cd-head{display:flex;align-items:center;justify-content:space-between;padding:18px 20px 12px;border-bottom:1px solid rgba(73,45,23,.08);}' +
      '#fiome-cd-head h2{font-size:1.35rem;margin:0;font-weight:800;}' +
      '#fiome-cd-close{background:none;border:none;cursor:pointer;font-size:1.5rem;line-height:1;color:#492d17;padding:4px;}' +
      '#fiome-cd-ship{padding:10px 20px 12px;border-bottom:1px solid rgba(73,45,23,.08);font-size:.86rem;text-align:center;}' +
      '#fiome-cd-shipbar{height:7px;background:rgba(73,45,23,.1);border-radius:99px;overflow:hidden;margin-top:8px;}' +
      '#fiome-cd-shipbar span{display:block;height:100%;background:#2f7a3e;border-radius:99px;transition:width .3s ease;}' +
      '#fiome-cd-items{flex:1;overflow-y:auto;padding:6px 20px;}' +
      '.fiome-cd-item{display:flex;gap:12px;padding:14px 0;border-bottom:1px solid rgba(73,45,23,.07);}' +
      '.fiome-cd-item img{width:64px;height:64px;object-fit:contain;border-radius:10px;background:#faf6e8;flex-shrink:0;}' +
      '.fiome-cd-mid{flex:1;min-width:0;}' +
      '.fiome-cd-title{font-weight:700;font-size:.95rem;margin:0 0 1px;}' +
      '.fiome-cd-var{color:#9a7a4e;font-size:.86rem;margin:0 0 8px;}' +
      '.fiome-cd-qty{display:inline-flex;align-items:center;gap:0;border:1px solid rgba(73,45,23,.18);border-radius:10px;overflow:hidden;}' +
      '.fiome-cd-qty button{width:38px;height:36px;border:none;background:#fff;cursor:pointer;font-size:1.1rem;color:#492d17;font-weight:700;}' +
      '.fiome-cd-qty button:disabled{opacity:.35;cursor:default;}' +
      '.fiome-cd-qty span{min-width:26px;text-align:center;font-size:.92rem;font-weight:700;}' +
      '.fiome-cd-right{text-align:right;display:flex;flex-direction:column;align-items:flex-end;gap:4px;}' +
      '.fiome-cd-was{color:#9a7a4e;text-decoration:line-through;font-size:.88rem;margin-right:6px;}' +
      '.fiome-cd-price{color:#2f7a3e;font-weight:800;font-size:.98rem;}' +
      '.fiome-cd-save{color:#2f7a3e;font-size:.78rem;font-weight:700;}' +
      '.fiome-cd-rm{background:none;border:none;cursor:pointer;color:#9a7a4e;font-size:.75rem;text-decoration:underline;padding:2px 0;margin-top:4px;}' +
      '.fcd-upg{display:block;width:100%;text-align:left;background:#eef6ee;border:1.5px solid #9acca1;border-radius:10px;padding:9px 12px;margin-top:9px;cursor:pointer;font-family:inherit;transition:background .15s ease;}' +
      '.fcd-upg:hover{background:#e2f0e4;}' +
      '.fcd-upg b{display:block;font-size:.8rem;color:#234f2e;}' +
      '.fcd-upg span{display:block;font-size:.68rem;color:#345c45;margin-top:2px;font-weight:600;}' +
      '#fiome-cd-freq{padding:12px 20px;border-top:1px solid rgba(73,45,23,.08);font-size:.86rem;}' +
      '#fiome-cd-freq .fcd-pill{border:1.5px solid rgba(73,45,23,.25);background:#fff;color:#492d17;border-radius:999px;padding:5px 12px;font-size:.78rem;font-weight:700;cursor:pointer;margin-left:6px;transition:background .15s ease,color .15s ease;}' +
      '#fiome-cd-freq .fcd-pill[aria-pressed=true]{background:#345c45;border-color:#345c45;color:#faf6e8;}' +
      '#fiome-cd-freq .fcd-pill:disabled{opacity:.35;cursor:not-allowed;}' +
      '#fiome-cd-freq .fcd-hint{font-size:.7rem;color:#9a7a4e;margin:5px 0 0;}' +
      '#fiome-cd-upsell{padding:12px 20px;background:#faf6e8;border-top:1px solid rgba(73,45,23,.08);}' +
      '#fiome-cd-upsell p{margin:0 0 8px;font-size:.82rem;font-weight:700;color:#345c45;}' +
      '.fcd-up{border:1.5px solid #9acca1;background:#fff;color:#492d17;border-radius:999px;padding:6px 11px;font-size:.76rem;font-weight:700;cursor:pointer;margin:0 6px 6px 0;font-family:inherit;}' +
      '.fcd-up:hover{background:#eef6ee;}' +
      '#fiome-cd-trust{text-align:center;font-size:.72rem;color:#9a7a4e;margin-top:8px;}' +
      '#fiome-cd-shipline{text-align:center;font-size:.8rem;font-weight:700;color:#2f7a3e;margin-top:9px;}' +
      '#fiome-cd-saving{text-align:right;font-size:.8rem;color:#2f7a3e;font-weight:700;margin:-8px 0 10px;}' +
      '#fiome-cd-empty a{display:inline-block;margin-top:14px;background:#e8a33d;color:#492d17;border-radius:999px;padding:12px 26px;font-weight:700;text-decoration:none;}' +
      '#fiome-cd-foot{padding:12px 20px 18px;border-top:1px solid rgba(73,45,23,.08);}' +
      '#fiome-cd-sub{display:flex;justify-content:space-between;align-items:baseline;font-weight:800;font-size:1.02rem;margin-bottom:12px;}' +
      '#fiome-cd-checkout{display:block;width:100%;text-align:center;background:#ef8f4f;color:#492d17;border:none;border-radius:999px;padding:15px 20px;font-weight:800;font-size:1.05rem;cursor:pointer;text-decoration:none;font-family:inherit;}' +
      '#fiome-cd-empty{text-align:center;color:#9a7a4e;padding:40px 20px;}' +
      '</style>' +
      '<div id="fiome-cd-overlay"></div>' +
      '<aside id="fiome-cd" role="dialog" aria-modal="true" aria-label="Cart">' +
      '  <div id="fiome-cd-head"><h2>Your cart (<span id="fiome-cd-count">0</span>)</h2><button id="fiome-cd-close" aria-label="Close cart">&times;</button></div>' +
      '  <div id="fiome-cd-ship"></div>' +
      '  <div id="fiome-cd-items"></div>' +
      '  <div id="fiome-cd-upsell"></div>' +
      '  <div id="fiome-cd-freq"></div>' +
      '  <div id="fiome-cd-foot"><div id="fiome-cd-sub"></div><div id="fiome-cd-saving"></div><button id="fiome-cd-checkout">Checkout</button><div id="fiome-cd-shipline"></div><div id="fiome-cd-trust">✓ Free shipping &nbsp;·&nbsp; ✓ Skip, pause, or cancel anytime — no questions asked</div></div>' +
      '</aside>';
    document.body.appendChild(root);
    root.querySelector('#fiome-cd-overlay').addEventListener('click', close);
    root.querySelector('#fiome-cd-close').addEventListener('click', close);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
    root.querySelector('#fiome-cd-checkout').addEventListener('click', function () {
      q = q.then(reconcile).then(function () { window.location.href = '/checkout'; });
    });
    /* delegated: steppers, remove, pills */
    root.addEventListener('click', function (e) {
      var step = e.target.closest('[data-cd-step]');
      if (step) {
        var key = step.getAttribute('data-cd-key'), d = +step.getAttribute('data-cd-step');
        apply(function () {
          return getCart().then(function (cart) {
            var it = cart.items.find(function (i) { return i.key === key; });
            if (!it) return;
            return post('/cart/change.js', { id: it.key, quantity: Math.max(0, it.quantity + d), selling_plan: it.selling_plan_allocation ? it.selling_plan_allocation.selling_plan.id : undefined });
          });
        });
        return;
      }
      var rm = e.target.closest('[data-cd-rm]');
      if (rm) {
        var rkey = rm.getAttribute('data-cd-rm');
        apply(function () { return post('/cart/change.js', { id: rkey, quantity: 0 }); });
        return;
      }
      var upgrade = e.target.closest('[data-cd-upgrade]');
      if (upgrade) {
        var ukey = upgrade.getAttribute('data-cd-upgrade');
        apply(function () {
          return getCart().then(function (cart) {
            var it = cart.items.find(function (i) { return i.key === ukey; });
            if (!it) return;
            var subs = cart.items.filter(function (i) { return i.selling_plan_allocation; });
            var newTotal = subs.reduce(function (s, i) { return s + i.quantity; }, 0) + it.quantity;
            var freq = chosenFreq || 30;
            var ok = allowedFreqs(newTotal);
            if (ok.indexOf(freq) === -1) freq = ok[ok.length - 1];
            return post('/cart/change.js', { id: it.key, quantity: it.quantity, selling_plan: PLANS[freq][tierOf(newTotal)] });
          });
        });
        return;
      }
      var up = e.target.closest('.fcd-up');
      if (up) {
        var vid = +up.getAttribute('data-cd-upsell');
        apply(function () {
          return getCart().then(function (cart) {
            var subs = cart.items.filter(function (i) { return i.selling_plan_allocation; });
            var total = subs.reduce(function (s, i) { return s + i.quantity; }, 0);
            var freq = chosenFreq || 30;
            var ok = allowedFreqs(total + 1);
            if (ok.indexOf(freq) === -1) freq = ok[ok.length - 1];
            return post('/cart/add.js', { items: [{ id: vid, quantity: 1, selling_plan: PLANS[freq][tierOf(total + 1)] }] });
          });
        });
        return;
      }
      var pill = e.target.closest('.fcd-pill');
      if (pill && !pill.disabled) {
        chosenFreq = +pill.getAttribute('data-f');
        /* optimistic paint */
        [].slice.call(root.querySelectorAll('.fcd-pill')).forEach(function (b) {
          b.setAttribute('aria-pressed', b === pill ? 'true' : 'false');
        });
        apply(function () { return Promise.resolve(); });
      }
    });
  }

  function open() { ensureShell(); document.documentElement.classList.add('fiome-cd-open'); refresh(); }
  function close() { document.documentElement.classList.remove('fiome-cd-open'); }

  function refresh() { q = q.then(getCart).then(render).catch(function () {}); return q; }

  /* ---------- render ---------- */
  function render(cart) {
    if (!cart) return;
    ensureShell();
    var subs = cart.items.filter(function (i) { return i.selling_plan_allocation; });
    var total = subs.reduce(function (s, i) { return s + i.quantity; }, 0);
    var freq = total ? (chosenFreq || FREQ_OF[subs[0].selling_plan_allocation.selling_plan.id] || 30) : 30;

    root.querySelector('#fiome-cd-count').textContent = cart.item_count;
    var badge = document.getElementById('cartCount');
    if (badge) badge.textContent = cart.item_count;

    /* free shipping bar */
    var ship = root.querySelector('#fiome-cd-ship');
    if (!cart.item_count) ship.innerHTML = '';
    else if (subs.length || cart.total_price >= FREE_SHIP_THRESHOLD) {
      ship.innerHTML = '🎉 <b>Congrats</b>—you’ve unlocked free shipping!' +
        '<div id="fiome-cd-shipbar"><span style="width:100%"></span></div>';
    } else {
      var rem = FREE_SHIP_THRESHOLD - cart.total_price;
      ship.innerHTML = 'You’re <b>' + money(rem) + '</b> away from free shipping' +
        '<div id="fiome-cd-shipbar"><span style="width:' + Math.min(100, cart.total_price / FREE_SHIP_THRESHOLD * 100) + '%"></span></div>';
    }

    /* items */
    var box = root.querySelector('#fiome-cd-items');
    if (!cart.items.length) {
      box.innerHTML = '<div id="fiome-cd-empty">Your cart is empty.<br>5g of fiber per bite is waiting.<br><a href="/products/fiber-bites">Shop Fiber Bites</a></div>';
    } else {
      box.innerHTML = cart.items.map(function (i) {
        var img = i.image ? i.image.replace(/(\.[a-z]+)(\?|$)/, '_200x$1$2') : '';
        var save = i.original_line_price > i.final_line_price
          ? Math.round((1 - i.final_line_price / i.original_line_price) * 100) : 0;
        /* one-time line of the subscription product → inline upgrade card with real dollar savings */
        var upg = '';
        if (!i.selling_plan_allocation && i.handle === SUB_HANDLE) {
          var wouldBe = total + i.quantity;                     /* bags if this line joins the sub */
          var per = SUB_PER[tierOf(wouldBe)];
          var delta = (ONE_TIME_PER - per) * i.quantity;
          upg = '<button type="button" class="fcd-upg" data-cd-upgrade="' + i.key + '">' +
            '<b>⟳ Upgrade to Subscribe &amp; Save — save ' + money(delta) + '</b>' +
            '<span>' + money(per) + '/bag · free shipping · skip, pause, or cancel anytime</span>' +
          '</button>';
        }
        return '<div class="fiome-cd-item">' +
          (img ? '<img src="' + img + '" alt="">' : '') +
          '<div class="fiome-cd-mid">' +
            '<p class="fiome-cd-title">' + i.product_title + '</p>' +
            '<p class="fiome-cd-var">' + (i.variant_title || '') + '</p>' +
            '<span class="fiome-cd-qty">' +
              '<button type="button" data-cd-step="-1" data-cd-key="' + i.key + '" aria-label="Decrease quantity">−</button>' +
              '<span>' + i.quantity + '</span>' +
              '<button type="button" data-cd-step="1" data-cd-key="' + i.key + '" aria-label="Increase quantity">+</button>' +
            '</span>' +
            upg +
          '</div>' +
          '<div class="fiome-cd-right">' +
            '<span>' + (save ? '<span class="fiome-cd-was">' + money(i.original_line_price) + '</span>' : '') +
              '<span class="fiome-cd-price">' + money(i.final_line_price) + '</span></span>' +
            (save ? '<span class="fiome-cd-save">Saving ' + save + '%</span>' : '') +
            '<button class="fiome-cd-rm" data-cd-rm="' + i.key + '">Remove</button>' +
          '</div>' +
        '</div>';
      }).join('');
    }

    /* tier upsell strip — the next discount level, one tap away */
    var upx = root.querySelector('#fiome-cd-upsell');
    if (!total) upx.innerHTML = '';
    else {
      var lead = total < 2 ? 'Add a 2nd bag → save 25% on everything'
               : (total < 3 ? 'Add a 3rd bag → save 30% on everything'
               : 'Add another flavor — 30% off applies to every bag');
      upx.innerHTML = '<p>' + lead + '</p>' + FLAVORS.map(function (f) {
        return '<button type="button" class="fcd-up" data-cd-upsell="' + f.id + '">+ ' + f.name + '</button>';
      }).join('');
    }

    /* frequency pills — locked options stay visible but hollowed, matching the PDP */
    var fq = root.querySelector('#fiome-cd-freq');
    if (!total) fq.innerHTML = '';
    else {
      var ok = allowedFreqs(total);
      fq.innerHTML = '<span style="font-weight:700;">Deliver every:</span>' +
        [30, 60, 90].map(function (f) {
          var en = ok.indexOf(f) !== -1;
          return '<button type="button" class="fcd-pill" data-f="' + f + '"' + (en ? '' : ' disabled') + ' aria-pressed="' + (f === freq ? 'true' : 'false') + '">' + f + ' days</button>';
        }).join('') +
        '<p class="fcd-hint">' + (total < 2 ? 'Add a 2nd bag to unlock 60-day delivery'
          : (total < 3 ? 'Add a 3rd bag to unlock 90-day delivery' : '')) + '</p>';
    }

    /* subtotal + savings + checkout total */
    var was = cart.items.reduce(function (s, i) { return s + i.original_line_price; }, 0);
    root.querySelector('#fiome-cd-sub').innerHTML =
      '<span>Subtotal (' + cart.item_count + ' item' + (cart.item_count === 1 ? '' : 's') + '):</span>' +
      '<span>' + (was > cart.total_price ? '<span class="fiome-cd-was">' + money(was) + '</span>' : '') +
      '<span class="fiome-cd-price" style="font-size:1.1rem;">' + money(cart.total_price) + '</span></span>';
    root.querySelector('#fiome-cd-saving').textContent =
      was > cart.total_price ? 'You’re saving ' + money(was - cart.total_price) : '';
    root.querySelector('#fiome-cd-checkout').textContent =
      cart.item_count ? 'Checkout — ' + money(cart.total_price) : 'Checkout';
    root.querySelector('#fiome-cd-trust').style.display = cart.item_count ? '' : 'none';
    /* delivery promise directly under the checkout CTA — answers "when will it arrive?"
       at the decision moment. Sun–Thu: ships by tomorrow; Fri/Sat: ships Monday. */
    var shipEl = root.querySelector('#fiome-cd-shipline');
    shipEl.style.display = cart.item_count ? '' : 'none';
    var day = new Date().getDay(); /* 0 Sun … 6 Sat */
    shipEl.innerHTML = '📦 Order now — ships by <b>' + (day === 5 || day === 6 ? 'Monday' : 'tomorrow') + '</b>';
  }

  /* ---------- global wiring ---------- */
  /* header cart icon opens the drawer instead of navigating */
  document.addEventListener('click', function (e) {
    var a = e.target.closest('#cartOpen, a[href="/cart"]');
    if (!a) return;
    e.preventDefault();
    open();
  });

  /* open after any add-to-cart; re-render (and reconcile) after any cart mutation */
  var origFetch = window.fetch;
  window.fetch = function (u) {
    var p = origFetch.apply(this, arguments);
    try {
      var url = (typeof u === 'string') ? u : (u && u.url) || '';
      if (/\/cart\/add/.test(url)) p.then(function () { apply(function () { return Promise.resolve(); }); setTimeout(open, 150); });
      else if (/\/cart\/(change|update|clear)/.test(url) && !/fiome-internal/.test(url)) { /* our own posts already funnel through apply() */ }
    } catch (e2) {}
    return p;
  };
  document.addEventListener('cart:refresh', function () { refresh(); });

  /* boot: reconcile any stale carts from previous sessions, keep badge fresh */
  apply(function () { return Promise.resolve(); });
})();
